import { api } from "./api";

export type SiteLanguage = "en" | "es";

export interface SiteAmenity { key: string; en: string; es: string; }
export interface SiteImage { url: string; altEn: string; altEs: string; credit?: string; }
export interface SiteFaq { id: string; question: string; answer: string; }
export interface SitePostCard { id: string; slug: string; title: string; excerpt: string; coverImageUrl?: string; publishedAt: string; }

export interface PublicSiteData {
  locale: SiteLanguage;
  propertyName: string;
  address?: string;
  phone?: string;
  publicEmail?: string;
  tagline: string;
  heroTitle: string;
  heroBody: string;
  aboutTitle: string;
  aboutBody: string;
  amenities: SiteAmenity[];
  heroImageUrl?: string;
  gallery: SiteImage[];
  bookingUrl?: string;
  whatsappUrl?: string;
  instagramUrl?: string;
  seoTitle: string;
  seoDescription: string;
  faqs: SiteFaq[];
  posts: SitePostCard[];
}

export interface PublicBlogPost {
  id: string;
  slug: string;
  locale: SiteLanguage;
  propertyName: string;
  title: string;
  excerpt: string;
  body: string;
  coverImageUrl?: string;
  seoTitle: string;
  seoDescription: string;
  publishedAt: string;
}

export function languageFromPath(): SiteLanguage {
  return window.location.pathname === "/es" || window.location.pathname.startsWith("/es/") ? "es" : "en";
}

export function localizedPath(path: string, language: SiteLanguage): string {
  const withoutLocale = path === "/es" ? "/" : path.replace(/^\/es(?=\/)/, "");
  if (language === "es") return withoutLocale === "/" ? "/es" : `/es${withoutLocale}`;
  return withoutLocale;
}

export function switchLanguage(language: SiteLanguage) {
  localStorage.setItem("site-language", language);
  window.location.assign(localizedPath(window.location.pathname, language));
}

export async function loadSite(language: SiteLanguage): Promise<PublicSiteData> {
  return api<PublicSiteData>(`/api/public/site?locale=${language}`);
}

export function setPageMeta(title: string, description: string, image?: string) {
  document.title = title;
  const setMeta = (selector: string, attribute: "name" | "property", key: string, value: string) => {
    let element = document.head.querySelector<HTMLMetaElement>(selector);
    if (!element) {
      element = document.createElement("meta");
      element.setAttribute(attribute, key);
      document.head.appendChild(element);
    }
    element.content = value;
  };
  setMeta('meta[name="description"]', "name", "description", description);
  setMeta('meta[property="og:title"]', "property", "og:title", title);
  setMeta('meta[property="og:description"]', "property", "og:description", description);
  if (image) setMeta('meta[property="og:image"]', "property", "og:image", image);
}
